/**
 * Analytics — Dashboards API Contracts
 *
 * Wire format for DashboardsController endpoints.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Dashboard widget / layout (arbitrary config blobs — typed loosely)
// ---------------------------------------------------------------------------

export const DashboardWidgetPositionSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
});

export const DashboardWidgetSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  config: z.record(z.unknown()),
  position: DashboardWidgetPositionSchema,
});
export type DashboardWidget = z.infer<typeof DashboardWidgetSchema>;

// ---------------------------------------------------------------------------
// Dashboard entity
// ---------------------------------------------------------------------------

export const DashboardSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  layout: z.array(z.record(z.string(), z.unknown())),
  isDefault: z.boolean(),
  isPublic: z.boolean(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const ListDashboardsQuerySchema = z.object({
  userId: z.string().optional(),
});
export type ListDashboardsQuery = z.infer<typeof ListDashboardsQuerySchema>;

export const CreateDashboardRequestSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  layout: z.array(z.record(z.string(), z.unknown())).optional(),
  isDefault: z.boolean().optional(),
  isPublic: z.boolean().optional(),
  createdBy: z.string().optional(),
});
export type CreateDashboardRequest = z.infer<typeof CreateDashboardRequestSchema>;

export const UpdateDashboardRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().optional(),
  layout: z.array(z.record(z.string(), z.unknown())).optional(),
  isDefault: z.boolean().optional(),
  isPublic: z.boolean().optional(),
});
export type UpdateDashboardRequest = z.infer<typeof UpdateDashboardRequestSchema>;
